import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submitConsultationRequest } from '../../services/contactApi';

type FormData = {
  name: string;
  email: string;
  message: string;
};

const ContactConsultationForm: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'email' ? value.trimStart() : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSubmitMessage('');

    const cleanedName = formData.name.replace(/\s+/g, ' ').trim();
    const cleanedEmail = formData.email.trim().toLowerCase();
    const cleanedMessage = formData.message.replace(/\s+/g, ' ').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (cleanedName.length < 2 || cleanedName.length > 80) {
      setFormError(t('contactSection.form.validation.nameLength'));
      return;
    }
    if (!emailRegex.test(cleanedEmail)) {
      setFormError(t('contactSection.form.validation.emailInvalid'));
      return;
    }
    if (cleanedMessage.length < 10 || cleanedMessage.length > 1000) {
      setFormError(t('contactSection.form.validation.messageLength'));
      return;
    }

    setIsSubmitting(true);
    try {
      await submitConsultationRequest({
        name: cleanedName,
        email: cleanedEmail,
        message: cleanedMessage,
      });
      setSubmitMessage(t('contactSection.form.success'));
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : t('contactSection.form.validation.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl p-8 border border-[#E2E8F0]">
      <h2 className="text-xl font-bold text-[#0F172A]">{t('contactSection.form.title')}</h2>
      <p className="text-[#64748B] text-sm mt-2">{t('contactSection.form.description')}</p>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-[#0F172A] mb-1.5">
            {t('contactSection.form.fields.name.label')}
          </label>
          <input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t('contactSection.form.fields.name.placeholder')}
            className="w-full px-4 py-3 border border-[#E2E8F0] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-colors bg-[#F7F8FA]"
            required
            minLength={2}
            maxLength={80}
            autoComplete="name"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-[#0F172A] mb-1.5">
            {t('contactSection.form.fields.email.label')}
          </label>
          <input
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            type="email"
            placeholder={t('contactSection.form.fields.email.placeholder')}
            className="w-full px-4 py-3 border border-[#E2E8F0] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-colors bg-[#F7F8FA]"
            required
            maxLength={254}
            autoComplete="email"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-[#0F172A] mb-1.5">
            {t('contactSection.form.fields.message.label')}
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={6}
            placeholder={t('contactSection.form.fields.message.placeholder')}
            className="w-full px-4 py-3 border border-[#E2E8F0] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-colors resize-none bg-[#F7F8FA]"
            required
            minLength={10}
            maxLength={1000}
          />
        </div>

        {formError && <p className="text-sm text-red-600">{formError}</p>}
        {submitMessage && <p className="text-sm text-teal-700">{submitMessage}</p>}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-teal-500 hover:bg-teal-400 text-white font-semibold py-3.5 transition-all text-sm disabled:opacity-50 hover:shadow-lg hover:shadow-teal-500/20"
        >
          {isSubmitting ? t('contactSection.form.submitting') : t('contactSection.form.submit')}
        </button>
      </form>
    </section>
  );
};

export default ContactConsultationForm;
